/**
 * Shared types for CloudKit Public catalog (CMS + Worker).
 * Cosmetic / reference only — never financial data.
 */

export type CatalogEntityType =
  | "CatalogMeta"
  | "Institution"
  | "Network"
  | "CardType"
  | "AccountType"
  | "CardProduct"
  | "MerchantIcon"
  | "InvestmentAsset";

/** Institution kind — banks, brokerages, government funds, insurers, transit, etc. */
export type InstitutionKind =
  | "bank"
  | "brokerage"
  | "government"
  | "insurer"
  | "transportation"
  | "other";

export const INSTITUTION_KINDS: readonly InstitutionKind[] = [
  "bank",
  "brokerage",
  "government",
  "insurer",
  "transportation",
  "other",
];

const INSTITUTION_KIND_SET = new Set<string>(INSTITUTION_KINDS);

export function normalizeInstitutionKind(raw: string | null | undefined): InstitutionKind | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  // Legacy records used a standalone "emi" kind; it folded into bank + bankType=ewallet.
  if (value === "emi") return "bank";
  // Accept common aliases for transit operators.
  if (value === "transport" || value === "transit") return "transportation";
  return INSTITUTION_KIND_SET.has(value) ? (value as InstitutionKind) : null;
}

/** Reads the JSON array stored in `AccountType.eligibleInstitutionKindsJSON`. */
export function parseInstitutionKindsJSON(raw: string | null | undefined): InstitutionKind[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const kinds = parsed
      .map((value) => (typeof value === "string" ? normalizeInstitutionKind(value) : null))
      .filter((value): value is InstitutionKind => Boolean(value));
    return Array.from(new Set(kinds));
  } catch {
    return [];
  }
}

/** Encodes eligible institution kinds; `null` means "no restriction". */
export function encodeInstitutionKinds(kinds: readonly string[]): string | null {
  const cleaned = Array.from(
    new Set(
      kinds
        .map((kind) => normalizeInstitutionKind(kind))
        .filter((kind): kind is InstitutionKind => Boolean(kind)),
    ),
  );
  return cleaned.length ? JSON.stringify(cleaned) : null;
}

/**
 * Extra kinds an institution offers beyond its primary `kind`
 * (e.g. a universal bank that also runs brokerage + insurer arms).
 * Primary is excluded; empty → `null`.
 */
export function encodeAdditionalInstitutionKinds(
  primary: InstitutionKind,
  kinds: readonly string[],
): string | null {
  const cleaned = Array.from(
    new Set(
      kinds
        .map((kind) => normalizeInstitutionKind(kind))
        .filter((kind): kind is InstitutionKind => Boolean(kind) && kind !== primary),
    ),
  );
  return cleaned.length ? JSON.stringify(cleaned) : null;
}

export function normalizeAdditionalInstitutionKinds(input: {
  kind: InstitutionKind;
  additionalKindsJSON?: string | null;
  additionalKinds?: string[] | null;
}): string | null {
  const fromArray = input.additionalKinds ?? [];
  const fromJSON = parseInstitutionKindsJSON(input.additionalKindsJSON);
  return encodeAdditionalInstitutionKinds(input.kind, [...fromArray, ...fromJSON]);
}

/** Bank sub-category — only meaningful when `kind === "bank"`. */
export type BankType =
  | "digital"
  | "ewallet"
  | "universal"
  | "thrift"
  | "neo"
  | "cooperative"
  | "rural"
  | "bnpl"
  | "other";

export type CatalogMeta = {
  recordName: string;
  schemaVersion: number;
  contentVersion: number;
  publishedAt: string | null;
};

export type Institution = {
  recordName: string;
  slug: string;
  name: string;
  /** Primary classification (bank, brokerage, transportation, …). */
  kind: InstitutionKind;
  bankType: BankType | null;
  /**
   * JSON array of extra `InstitutionKind` values this org also offers
   * (excludes primary `kind`). `null` / `[]` = none.
   */
  additionalKindsJSON: string | null;
  countryCode: string;
  appStoreAppID: string | null;
  appStoreURL: string | null;
  logoURL: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type Network = {
  recordName: string;
  slug: string;
  name: string;
  logoURL: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type CardType = {
  recordName: string;
  slug: string;
  name: string;
  mapsToKind: string | null;
  sortOrder: number;
  isActive: boolean;
};

/**
 * How an account-type template constrains currency when creating a private cash,
 * savings, or investment account. Cosmetic / reference only — never a user’s chosen ledger currency.
 */
export type AccountCurrencyMode = "none" | "fixed" | "selectable";

export type AccountType = {
  recordName: string;
  slug: string;
  name: string;
  /** Bridge to iOS `PaymentMethodKind` raw: `cash` / `savings` / `investment` / `other`. */
  mapsToKind: string | null;
  /** Template currency policy. Optional for schema-v5 caches. */
  currencyMode: AccountCurrencyMode;
  /** When `currencyMode === "fixed"`. */
  fixedCurrencyCode: string | null;
  /** JSON array of ISO codes when `currencyMode === "selectable"`. */
  allowedCurrencyCodesJSON: string | null;
  /**
   * JSON array of `InstitutionKind` values the iOS picker should offer for this template
   * (e.g. `["brokerage"]` for investments). `null` means every kind is eligible.
   */
  eligibleInstitutionKindsJSON: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type CardProduct = {
  recordName: string;
  institutionRecordName: string | null;
  networkRecordName: string | null;
  cardTypeRecordName: string | null;
  institutionSlug: string | null;
  networkSlug: string | null;
  cardTypeSlug: string | null;
  productName: string;
  /** Stable filename-safe id; empty on legacy rows until next save. */
  productSlug: string;
  tier: string;
  gradientJSON: string;
  cardImageURL: string | null;
  logoURL: string | null;
  /** JSON array of Wallet / Shortcuts “Card or Pass” display-name aliases. */
  walletMatchNamesJSON: string | null;
  sourcePageURL: string | null;
  notes: string | null;
  isVerified: boolean;
  isActive: boolean;
  sortOrder: number;
};

/**
 * Apple Wallet / Apple Card / Shortcuts category filter (slug).
 * Display labels live in `WALLET_CATEGORY_LABELS`. Catalog metadata only —
 * does not replace the on-device Category list.
 */
export type WalletCategory =
  | "foodAndDrink"
  | "shopping"
  | "entertainment"
  | "services"
  | "travel"
  | "transportation"
  | "health"
  | "other";

export const WALLET_CATEGORIES: readonly WalletCategory[] = [
  "foodAndDrink",
  "shopping",
  "entertainment",
  "services",
  "travel",
  "transportation",
  "health",
  "other",
];

export const WALLET_CATEGORY_LABELS: Record<WalletCategory, string> = {
  foodAndDrink: "Food & Drinks",
  shopping: "Shopping",
  entertainment: "Entertainment",
  services: "Services",
  travel: "Travel",
  transportation: "Transportation",
  health: "Health",
  other: "Other",
};

const WALLET_CATEGORY_SET = new Set<string>(WALLET_CATEGORIES);

/** Provenance of a MerchantIcon row. */
export type MerchantSourceKind = "simple-icons" | "nsi" | "osm-wiki" | "cms" | "institution";

export const MERCHANT_SOURCE_KINDS: readonly MerchantSourceKind[] = [
  "simple-icons",
  "nsi",
  "osm-wiki",
  "cms",
  "institution",
];

const MERCHANT_SOURCE_KIND_SET = new Set<string>(MERCHANT_SOURCE_KINDS);

export type MerchantIcon = {
  recordName: string;
  displayName: string;
  normalizedName: string;
  /** JSON array of alias strings for on-device matching */
  matchTermsJSON: string;
  sourceSlug: string;
  sourceName: string;
  brandHex: string | null;
  iconURL: string | null;
  /**
   * Readable Wallet label mirrored from `walletCategory` (e.g. `Entertainment`).
   * Legacy rows may still hold a free-form hint until republished.
   */
  categoryHint: string | null;
  /** Apple Wallet category slug. */
  walletCategory: WalletCategory;
  /** OSM-style key/value (e.g. `amenity/fast_food`) or `cms/…` fallback. */
  granularKind: string;
  /** JSON array of ISO 3166-1 alpha-2 codes and/or `001` (world). */
  countryScopeJSON: string | null;
  wikidataId: string | null;
  nsiId: string | null;
  sourceKind: MerchantSourceKind;
  /** If this row was merged away, the surviving `recordName`. */
  mergedIntoRecordName: string | null;
  isActive: boolean;
  sortOrder: number;
};

export function normalizeWalletCategory(raw: string | null | undefined): WalletCategory | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value) return null;
  const slug = value.replace(/[\s_-]+/g, "");
  const compact = slug.toLowerCase();
  if (WALLET_CATEGORY_SET.has(value)) return value as WalletCategory;
  if (WALLET_CATEGORY_SET.has(compact)) return compact as WalletCategory;
  if (compact === "foodanddrink" || compact === "fooddrinks" || compact === "fooddrink") {
    return "foodAndDrink";
  }
  return null;
}

/**
 * Maps a legacy `categoryHint` (Apple label or slug) onto a Wallet category.
 * Unmapped hints such as `Bills` become `other`.
 */
export function walletCategoryFromHint(raw: string | null | undefined): WalletCategory {
  const fromSlug = normalizeWalletCategory(raw);
  if (fromSlug) return fromSlug;
  const compact = (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[&]/g, "and")
    .replace(/[^a-z0-9]+/g, "");
  if (!compact) return "other";
  const labels: Record<string, WalletCategory> = {
    foodanddrinks: "foodAndDrink",
    fooddrinks: "foodAndDrink",
    food: "foodAndDrink",
    shopping: "shopping",
    entertainment: "entertainment",
    services: "services",
    travel: "travel",
    transportation: "transportation",
    transport: "transportation",
    health: "health",
    other: "other",
  };
  return labels[compact] ?? "other";
}

export function walletCategoryLabel(category: WalletCategory): string {
  return WALLET_CATEGORY_LABELS[category];
}

/** Fallback OSM-style kind when a brand has no source tag. */
export function defaultGranularKind(category: WalletCategory): string {
  return `cms/${category}`;
}

export function normalizeMerchantSourceKind(
  raw: string | null | undefined,
  fallbackName?: string | null,
): MerchantSourceKind {
  const value = (raw ?? "").trim().toLowerCase();
  if (MERCHANT_SOURCE_KIND_SET.has(value)) return value as MerchantSourceKind;
  const fromName = (fallbackName ?? "").trim().toLowerCase();
  if (fromName === "simple-icons" || fromName === "simpleicons") return "simple-icons";
  if (fromName === "nsi" || fromName === "name-suggestion-index") return "nsi";
  if (fromName === "osm-wiki" || fromName === "osm wiki") return "osm-wiki";
  if (fromName === "institution") return "institution";
  return "cms";
}

/** Primary match key: lowercase, punctuation folded to spaces. */
export function normalizeMerchantName(raw: string | null | undefined): string {
  return (raw ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function merchantSourceSlug(raw: string | null | undefined): string {
  return (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function parseMatchTermsJSON(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return Array.from(
      new Set(parsed.map((value) => String(value).trim().toLowerCase()).filter(Boolean)),
    );
  } catch {
    return [];
  }
}

export function encodeMatchTerms(terms: readonly string[]): string {
  return JSON.stringify(
    Array.from(new Set(terms.map((term) => term.trim().toLowerCase()).filter(Boolean))),
  );
}

export function parseCountryScopeJSON(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return Array.from(
      new Set(
        parsed
          .map((value) => String(value).trim().toLowerCase())
          .filter((value) => /^[a-z]{2}$/.test(value) || value === "001"),
      ),
    );
  } catch {
    return [];
  }
}

export function encodeCountryScope(codes: readonly string[]): string | null {
  const cleaned = parseCountryScopeJSON(JSON.stringify(codes));
  return cleaned.length ? JSON.stringify(cleaned) : null;
}

export function countryScopeIsPH(codes: readonly string[]): boolean {
  return codes.includes("ph") && !codes.includes("001");
}

export function countryScopeIsGlobal(codes: readonly string[]): boolean {
  return codes.includes("001");
}

/** Tradable asset class an `InvestmentAsset` describes. */
export type InvestmentAssetClass = "stock" | "crypto";

/**
 * Reference metadata for a tradable asset (ticker, venue, quote currency, quote provider).
 * Catalog master data only — holdings, quantities, cost basis, and valuations stay in the
 * user's private CloudKit database and must never appear here.
 */
export type InvestmentAsset = {
  recordName: string;
  /** Stable identifier / slug, e.g. `btc`, `aapl`, `bdo-pse`. */
  assetId: string;
  name: string;
  /** Ticker or trading symbol, e.g. `BTC`, `AAPL`, `BDO`. */
  symbol: string;
  assetClass: InvestmentAssetClass;
  /** ISO 10383 market identifier for listed assets (e.g. `XPHS`); `null` for crypto. */
  exchangeMIC: string | null;
  /** Currency the asset is quoted in — not the user's account currency. */
  quoteCurrencyCode: string;
  /** Quote source slug, e.g. `coingecko`, `pse`. */
  providerId: string | null;
  isActive: boolean;
  sortOrder: number;
};

/** Snapshot shape cached on device and returned by GET /api/catalog/snapshot */
export type CatalogSnapshot = {
  schemaVersion: number;
  contentVersion: number;
  publishedAt: string | null;
  fetchedAt: string;
  institutions: Institution[];
  networks: Network[];
  cardTypes: CardType[];
  accountTypes: AccountType[];
  products: CardProduct[];
  merchantIcons: MerchantIcon[];
  investmentAssets: InvestmentAsset[];
};

export type SeedPayload = {
  schemaVersion: number;
  institutions: Array<{
    slug: string;
    name: string;
    kind?: InstitutionKind;
    bankType?: BankType | null;
    additionalKindsJSON?: string | null;
    additionalKinds?: InstitutionKind[];
    countryCode?: string;
    appStoreAppID?: string | null;
    appStoreURL?: string | null;
    sortOrder?: number;
    logoURL?: string | null;
  }>;
  networks: Array<{
    slug: string;
    name: string;
    sortOrder?: number;
    logoURL?: string | null;
  }>;
  cardTypes: Array<{
    slug: string;
    name: string;
    mapsToKind?: string | null;
    sortOrder?: number;
  }>;
  accountTypes: Array<{
    slug: string;
    name: string;
    mapsToKind?: string | null;
    currencyMode?: AccountCurrencyMode;
    fixedCurrencyCode?: string | null;
    allowedCurrencyCodesJSON?: string | null;
    allowedCurrencyCodes?: string[];
    eligibleInstitutionKindsJSON?: string | null;
    eligibleInstitutionKinds?: string[];
    sortOrder?: number;
  }>;
  products: Array<{
    recordName?: string;
    institutionSlug: string;
    networkSlug: string;
    cardTypeSlug: string;
    productName: string;
    productSlug?: string;
    tier?: string;
    gradientJSON?: string;
    cardImageURL?: string | null;
    logoURL?: string | null;
    walletMatchNamesJSON?: string | null;
    walletMatchNames?: string[];
    sourcePageURL?: string | null;
    notes?: string | null;
    isVerified?: boolean;
    sortOrder?: number;
  }>;
  merchantIcons?: Array<{
    recordName?: string;
    displayName: string;
    normalizedName: string;
    matchTerms?: string[];
    matchTermsJSON?: string;
    sourceSlug: string;
    sourceName?: string;
    brandHex?: string | null;
    iconURL?: string | null;
    categoryHint?: string | null;
    walletCategory?: WalletCategory | string | null;
    granularKind?: string | null;
    countryScopeJSON?: string | null;
    countryScope?: string[] | null;
    wikidataId?: string | null;
    nsiId?: string | null;
    sourceKind?: MerchantSourceKind | string | null;
    mergedIntoRecordName?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  }>;
  investmentAssets?: Array<{
    recordName?: string;
    assetId?: string;
    /** Accepted alias for `assetId` for parity with the other catalog collections. */
    slug?: string;
    name: string;
    symbol: string;
    assetClass?: InvestmentAssetClass;
    /** Accepted alias for `assetClass`. */
    class?: InvestmentAssetClass;
    exchangeMIC?: string | null;
    quoteCurrencyCode?: string;
    providerId?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  }>;
};
